import { Inject } from '@odinjs/odin'
import moment from 'moment';

import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ButtonAPI, FormAPI, InputAPI, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';

import { FragmentNames, RecommendationExecuteTimeModalAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import timeService from '../../../../services/time-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import { ApplicationRoute } from '../../../../constants/routes';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import { YesNoType } from '../../../../utils/enum-utils';
import { Conditionals, DisplayMessages } from '../administration-schedule/administration-schedule-types';
import { ConsistAdministrationMarginInfo } from './consist-administration-margin-info';
import MedicationService, {
  AllowedAdministerItemParams,
  CreatePrescriptionScheduleChangeRequest,
  CreatePrescriptionScheduleChangeResponse
} from '../../services/medication-service';

@Controller({ fragment: FragmentNames.RECOMMENDATION_EXECUTE_TIME_MODAL })
export default class RecommendationExecuteTimeModalController extends BaseController {

  @Inject(RecommendationExecuteTimeModalAliases.INFORMATION_FORM)
  private recommendationFormApi!: FormAPI;

  @Inject(RecommendationExecuteTimeModalAliases.BUTTON_EXECUTE)
  private executeButtonApi!: ButtonAPI;

  @OnMount(RecommendationExecuteTimeModalAliases.DATE_HOUR_INPUT)
  handleMountDateHourInput({ target }: EventData<InputAPI>): void {
    timeService.getCurrentDateTime()
      .then(date => target.setValue(date))
      .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnMount([RecommendationExecuteTimeModalAliases.TEXT_RECOMMENDATION_DATE, RecommendationExecuteTimeModalAliases.TEXT_RECOMMENDATION_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':recommendation-information', data.description as string)
      .replace(':recommendation-date', DateFormatterUtil.timeStamp(String(data.start)));

    target.setValue(value);
  }

  @OnClick(RecommendationExecuteTimeModalAliases.BUTTON_EXECUTE)
  handleClickExecuteButton(): void {
    this.isLoading(true);
    const { currentTime, justification } = this.recommendationFormApi.getRecord();
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    this.validateWithParam13()
      .then(() => ConsistAdministrationMarginInfo.execute({
        administrationDate: currentTime as string,
        itemType: PrescriptionType.RECOMMENDATIONS,
        justification: justification as string | undefined,
        scheduleSequenceNumber: scheduleSequenceNumber as string
      }))
      .then(response => {
        if (response.responseDescription) {
          this.showToast(response.responseDescription, ToastLevel.WARNING, ToastType.BANNER);
          return Promise.reject(false);
        }
        return this.doesAllowAdministerItem();
      })
      .then((isAllowed) => {
        if (isAllowed) {
          this.showToast('text.perform-with-success', ToastLevel.SUCCESS, ToastType.BANNER);
        }
        this.isLoading(false);
        this.redirectUserToTimeline();
      })
      .catch(error => {
        this.informationsCatch(error);
      })
  }

  private informationsCatch(error: any): void {
    this.isLoading(false);
    const hasError = !!error;

    if (!hasError) {
      return
    }

    if (error.body?.errors) {
      error?.body?.errors.forEach((message: string) => {
        this.showToast(message, ToastLevel.WARNING, ToastType.BANNER)
      })
      return;
    }

    if (error.body) {
      this.showToast(error.body, ToastLevel.ERROR, ToastType.BANNER);
    }
  }

  private showToast(message: string, toastLevel: ToastLevel, type: ToastType): void {
    const toastId = 'recommendation-execute-time-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: toastId,
      level: toastLevel,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type,
    });
  }

  private validateWithParam13(): Promise<boolean> {
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    return new Promise((resolve) => resolve(param13 === 'EJ'));
  }

  private createPrescription(): Promise<CreatePrescriptionScheduleChangeResponse> {
    const { currentTime, justification, observation } = this.recommendationFormApi.getRecord();
    const { encounterId, scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const timelineRecommendation = TimelineContext.get() as TimeLineEventMedication;
    const param22 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY) as number;
    const { intervalCode, itemSequence } = this.context.getBulkData();
    const dateTimeCurrent = moment(String(currentTime)).format(ISO_FULL_DATE_FORMAT);
    const itemQuantity = 1;
    const functionCode = 9129;

    const body: CreatePrescriptionScheduleChangeRequest = {
      administrationRouteType: '',
      componentsDescription: '',
      encounterNumber: Number(encounterId),
      eventDate: dateTimeCurrent,
      functionCode: functionCode,
      groupingNumber: timelineRecommendation.groupingNumber,
      intervalCode: String(intervalCode),
      isGenerateAll: '',
      itemCode: String(timelineRecommendation.itemCode),
      itemDescription: String(timelineRecommendation.description),
      itemObservationDescription: '',
      itemQuantity: itemQuantity,
      itemSequence: Number(itemSequence),
      itemType: timelineRecommendation.itemType,
      justifyDescription: justification ? String(justification) : '',
      messageDescriptionResponse: '',
      observationDescription: observation ? String(observation) : '',
      pdoPrn: YesNoType.NO,
      prescriptionHourQuantity: param22,
      prescriptionNumber: Number(timelineRecommendation.prescriptionNumber),
      prescriptionsNumber: String(timelineRecommendation.prescriptionNumber),
      scheduleSequence: Number(scheduleSequenceNumber),
      suspendPrescribedMaterial: '',
      unitMeasureCode: '',
      uoMCodeQuantityAdministered: '',
    }
    return MedicationService.createPrescriptionScheduleChange(body)
  }

  private redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.RECOMMENDATION_EXECUTE_TIME_MODAL);
  }

  private isLoading(state: boolean): void {
    this.executeButtonApi.setEnabled(!state);
    this.recommendationFormApi.setLoading(state);
  }

  private doesAllowAdministerItem(): Promise<boolean> {
    const userName = foundationContext.getUser()?.username;
    const profileCode = foundationContext.getProfile()?.id;
    const sectorCode = foundationContext.getSector()?.id;
    const internalProcessSequence = TimelineContext.get().internalProcedureSequence;
    const itemCode = TimelineContext.get().itemCode;

    const params: AllowedAdministerItemParams = {
      internalProcessSequence,
      isPalm: Conditionals.POSITIVE,
      itemCode: itemCode,
      itemType: PrescriptionType.RECOMMENDATIONS,
      originProcedureType: 0,
      profileCode,
      sectorCode,
      userName,
    }

    return MedicationService.doesAllowAdministerItem(params)
      .then((response) => {
        if (!response) {
          MedicationService.showToast(DisplayMessages.REC_NOT_ALLOWED, ToastLevel.ERROR);
          return false;
        }
        this.createPrescription()
        return response
      })
      .catch(error => {
        this.informationsCatch(error);
        return false;
      })
  }
}
